import React from 'react';
import PropTypes from 'prop-types';
import SiteLowerFooterLogo from './SiteLowerFooterLogo/SiteLowerFooterLogo';
import SiteLowerFooterMenu from './SiteLowerFooterMenu/SiteLowerFooterMenu';
import SiteLowerFooterCopyright from './SiteLowerFooterCopyright/SiteLowerFooterCopyright';
import './SiteLowerFooter.scss';
import { useRootSelector } from '../../../../../store/hooks';
import { selectTenantConfig } from '../../../../../store/features/tenantConfig';
import { TenantDomainId } from '../../../../../store/features/pageConfig';

const informaConnectLogo = {
  link: 'https://informaconnect.com/',
  url: '/images/informa-white-logo.png',
  alt: 'Informa',
};

const informaTechTargetLogo = {
  link: 'https://omdia.tech.informa.com/',
  url: '/images/informa-techtarget-logo-footer@2x.png',
  alt: 'Informa Tech Target',
};

const informaConnectHomeLink = {
  title: 'event.home-page',
  href: 'https://informaconnect.com/',
  target: '_blank',
};

const informaTechHomeLink = {
  title: 'event.home-page',
  href: 'https://tech.informa.com/',
  target: '_blank',
};

const siteContactLink = {
  title: 'event.contact',
  href: '/contact/',
};

const informaPrivacyPolicyLink = {
  title: 'event.privacy-centre',
  href: 'https://www.informa.com/privacy-policy/',
  target: '_blank',
};

const informaAccessibilityLink = {
  title: 'informa.accessibility',
  href: 'https://www.informamarkets.com/en/accessibility.html',
  target: '_blank',
};

const parentTermsOfUseLink = {
  title: 'event.terms-of-use',
  href: '/terms-of-use',
  parent: true,
};

const siteTermsAndConditionsLink = {
  title: 'event.terms-and-conditions',
  href: '/terms-and-conditions',
};

const informaTermsAndConditionsLink = {
  title: 'event.terms-and-conditions',
  href: 'https://informaconnect.com/terms-and-policies/',
  target: '_blank',
};

const ccpaLink = {
  title: 'event.ccpa',
  href: 'https://privacyportal-eu-cdn.onetrust.com/dsarwebform/c1f53e84-9f05-4169-a854-85052b63c50b/5f26b553-52cc-4973-a761-295d5634a6b6.html',
  target: '_blank',
};

const legacyFanExpoFooter = {
  logo: informaConnectLogo,
  menu: [
    {
      title: 'Privacy Policy',
      href: 'https://www.informa.com/generic-content/privacy-policy/',
    },
    {
      title: 'Cookie Policy',
      href: 'https://www.informa.com/generic-content/cookie-policy/',
    },
    {
      title: 'Terms of Use',
      href: 'https://www.informa.com/generic-content/terms--conditions/',
    },
  ],
};

const informaTechFooter = {
  logo: informaConnectLogo,
  menu: [
    informaTechHomeLink,
    siteContactLink,
    informaPrivacyPolicyLink,
    parentTermsOfUseLink,
    informaTermsAndConditionsLink,
    ccpaLink,
  ],
};

const gdcFooter = {
  logo: informaConnectLogo,
  menu: [
    informaTechHomeLink,
    siteContactLink,
    informaPrivacyPolicyLink,
    parentTermsOfUseLink,
    ccpaLink,
  ],
};

const theWasteSummitFooter = {
  logo: informaConnectLogo,
  menu: [
    informaAccessibilityLink,
    informaPrivacyPolicyLink,
    {
      title: 'event.terms-of-use',
      href: 'https://www.informamarkets.com/en/terms-of-use.html',
      target: '_blank',
    },
    {
      title: 'event.terms-and-conditions',
      href: 'https://www.informamarkets.com/en/visitor-terms-and-conditions.html',
      target: '_blank',
    },
    {
      title: 'event.code-of-conduct',
      href: 'https://www.informamarkets.com/en/event-code-of-conduct.html',
      target: '_blank',
    },
  ],
};

const omdiaFooter = {
  logo: informaTechTargetLogo,
  menu: [
    {
      title: 'event.home-page',
      href: 'https://omdia.tech.informa.com/',
    },
    siteContactLink,
    {
      title: 'Privacy Policy',
      href: 'https://www.techtarget.com/privacy-policy/',
      target: '_blank',
    },
    {
      title: 'event.terms-of-use',
      href: 'https://www.techtarget.com/about-us/privacy-and-security-trust-center/',
      target: '_blank',
    },
    {
      title: 'event.terms-and-conditions',
      href: 'https://www.techtarget.com/informa-tech-terms-conditions/',
      target: '_blank',
    },
    {
      title: 'event.ccpa',
      href: 'https://techtarget.zendesk.com/hc/en-us/requests/new?ticket_form_id=360001746954',
      target: '_blank',
    },
  ],
};

// Grouped tenant IDs
const fanExpoTenants = [
  TenantDomainId.FANEXPOHQ,
  TenantDomainId.ONEOFAKINDSHOW,
  TenantDomainId.INTERIORDESIGNSHOW,
  TenantDomainId.ARTTORONTO,
  TenantDomainId.THEARTISTSPROJECT,
  TenantDomainId.MIDDLEEASTFILMCOMICCON,
];

const techTenants = [
  TenantDomainId.NETWORKXEVENT,
  TenantDomainId.NETWORKXSERIES,
  TenantDomainId.KNECT365,
  TenantDomainId.TMTKNECT365,
  TenantDomainId.TELECOMSTECHACADEMY,
  TenantDomainId.AUTOMOTIVE,
  TenantDomainId.MSPSUMMIT,
  TenantDomainId.IWCE,
];

function getDefaultFooter(tenantId) {
  let termsOfUseLink;

  /* istanbul ignore next */
  switch (tenantId) {
    case TenantDomainId.SMHSCHOOLS:
      termsOfUseLink = {
        title: 'event.terms-of-use',
        href: 'https://informaconnect.com/terms-and-policies/',
        target: '_blank',
      };
      break;
    default:
      termsOfUseLink = parentTermsOfUseLink;
      break;
  }

  return {
    logo: informaConnectLogo,
    menu: [
      informaConnectHomeLink,
      siteContactLink,
      informaPrivacyPolicyLink,
      termsOfUseLink,
      siteTermsAndConditionsLink,
      ccpaLink,
    ],
  };
}

function getLowerFooterConfig(tenantId) {
  switch (tenantId) {
    case TenantDomainId.OMDIAEVENTS:
      return omdiaFooter;

    case TenantDomainId.GAMEDEVELOPERSCONFERENCE:
      return gdcFooter;

    case TenantDomainId.THEWASTESUMMIT:
      return theWasteSummitFooter;

    default:
      if (fanExpoTenants.includes(tenantId)) {
        return legacyFanExpoFooter;
      }

      if (techTenants.includes(tenantId)) {
        return informaTechFooter;
      }

      return getDefaultFooter(tenantId);
  }
}

/**
 * WHITE LABEL
 */
export default function SiteLowerFooter({ bgColor, logoStyles }) {
  const { id: tenantId } = useRootSelector(selectTenantConfig);
  const lowerFooter = getLowerFooterConfig(tenantId);

  return (
    <div
      className="footer-lower"
      style={{ backgroundColor: bgColor }}
      data-testid="lower-footer"
    >
      <div className="container">
        <div className="footer-lower__wrapper">
          <div className="footer-lower__logo" style={logoStyles}>
            <SiteLowerFooterLogo lowerFooter={lowerFooter} />
          </div>
          <div className="footer-lower__menu">
            <SiteLowerFooterMenu lowerFooter={lowerFooter} />
          </div>
        </div>
        <SiteLowerFooterCopyright />
      </div>
    </div>
  );
}

SiteLowerFooter.propTypes = {
  bgColor: PropTypes.string,
  logoStyles: PropTypes.object,
};
